import { isStudioWorkLocked } from '@/features/studio-renew/lib/publication'
import {
  createCharacter,
  createChat,
  createCommand,
  createStatus,
  createWorld,
} from '@/features/studio-renew/services/studio-crud.service'
import { importPatch } from '@/features/studio-renew/lib/studio-import'
import { SERVICE_COMMAND_KEYS } from '@/features/studio-renew/official-commands'
import { STATUS_THEMES } from '@/features/studio-renew/status-themes'
import { newRowId, useRenewStore } from '@/features/studio-renew/store'
import { flushAll } from '@/features/studio-renew/sync'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type {
  ImportBlock,
  ImportKind,
  ImportPatch,
} from '@/features/studio-renew/lib/studio-import'
import type { EditorKind } from '@/features/studio-renew/steps'
import type {
  Character,
  Chat,
  Command,
  Start,
  StatusBoard,
  World,
} from '@/features/studio-renew/types'

export type ImportTarget = {
  kind: EditorKind
  id?: string
  rootCandidate?: string
  baseRevision?: string
}
export type ImportChoice = ImportBlock & {
  root: boolean
  selected: boolean
  fields: { key: string; label: string; value: string; selected: boolean }[]
}
const COLLECTION = {
  work: 'chats',
  character: 'characters',
  world: 'worlds',
  status: 'statuses',
  command: 'commands',
} as const
export function importEntity(target: ImportTarget) {
  return target.id
    ? useRenewStore.getState()[COLLECTION[target.kind]][target.id]
    : undefined
}
function previous<T extends object>(entity: T, patch: Partial<T>): Partial<T> {
  const old: Partial<T> = {}
  for (const key of Object.keys(patch) as (keyof T)[]) old[key] = entity[key]
  return old
}
function selectedValues(choice: ImportChoice) {
  return Object.fromEntries(
    choice.fields
      .filter((field) => field.selected)
      .map((field) => [field.key, field.value])
  )
}
function update(target: ImportTarget, patch: ImportPatch) {
  const state = useRenewStore.getState()
  if (!target.id) throw new Error('적용할 원본을 찾지 못했어요.')
  if (target.kind === 'character')
    state.updateCharacter(target.id, patch.character ?? {})
  if (target.kind === 'world') state.updateWorld(target.id, patch.world ?? {})
  if (target.kind === 'status')
    state.updateStatus(target.id, patch.status ?? {})
  if (target.kind === 'command')
    state.updateCommand(target.id, patch.command ?? {})
  if (target.kind === 'work') state.updateChat(target.id, patch.work ?? {})
}
function rootValues(kind: EditorKind, patch: ImportPatch) {
  if (kind === 'character') return patch.character ?? {}
  if (kind === 'world') return patch.world ?? {}
  if (kind === 'status') return patch.status ?? {}
  if (kind === 'command') return patch.command ?? {}
  return patch.work ?? {}
}
function materialName(kind: EditorKind, patch: ImportPatch) {
  const name: unknown = Reflect.get(rootValues(kind, patch), 'name')
  return typeof name === 'string' ? name.trim() : ''
}
function matchesPatch(target: ImportTarget, patch: ImportPatch) {
  const entity = importEntity(target)
  return (
    !!entity &&
    Object.entries(rootValues(target.kind, patch)).every(
      ([key, value]) =>
        JSON.stringify(Reflect.get(entity, key)) === JSON.stringify(value)
    )
  )
}

/** 검토한 불변 계획을 실행한다. 재시도는 이미 발급받은 서버 ID와 저장 단계를 재사용한다. */
export function importApplication(
  target: ImportTarget,
  choices: ImportChoice[],
  fingerprint: string,
  isCurrent: () => boolean
) {
  const owner = useRenewStore.getState().owner
  const identity = useSessionStore.getState().identity
  const original = importEntity(target)
  const plans = choices
    .filter(
      (choice) =>
        choice.selected && choice.fields.some((field) => field.selected)
    )
    .map((choice) => ({
      ...choice,
      patch: importPatch(choice.kind, selectedValues(choice)),
    }))
  if (!plans.length) throw new Error('가져올 항목을 선택해 주세요.')
  const root: ImportPatch = plans.find((plan) => plan.root)?.patch ?? {}
  const related = plans.filter((plan) => !plan.root)
  const ids = new Map<string, string>()
  let applied = false
  let restored = false
  let undoPatch: ImportPatch | null = null
  let activeTarget = { ...target }
  let keys: string[] = []
  const current = () => {
    const session = useSessionStore.getState()
    if (
      !isCurrent() ||
      !owner ||
      useRenewStore.getState().owner !== owner ||
      session.identity !== identity ||
      session.data?.user?.id !== owner ||
      session.data?.user?.guest
    )
      throw new Error('계정이나 편집 대상이 변경됐어요.')
  }
  const unchanged = () => {
    current()
    if (target.id && JSON.stringify(importEntity(target)) !== fingerprint)
      throw new Error(
        '원본이 변경됐어요. 만든 재료는 목록에 보관했으니 현재 내용을 확인해 주세요.'
      )
  }
  const group = (kind: ImportKind) =>
    related.filter((plan) => plan.kind === kind)
  if (group('world').length > 1 || group('status').length > 1)
    throw new Error('세계관과 상태창은 각각 하나씩 가져올 수 있어요.')
  if (
    target.kind !== 'work' &&
    related.some((plan) => target.kind !== 'character' || plan.kind !== 'world')
  )
    throw new Error(
      '전체 구성을 함께 가져오려면 적용 대상을 “새 작품”으로 선택해 주세요. 각 재료만 따로 가져올 수도 있어요.'
    )
  if (
    !target.id &&
    !materialName(target.kind, root) &&
    target.kind !== 'command'
  )
    throw new Error('새로 만들 재료의 이름을 선택해 주세요.')
  if (!target.id && target.kind === 'command' && !root.command?.key)
    throw new Error('새 명령어의 키를 선택해 주세요.')
  for (const plan of related) {
    if (
      plan.kind !== 'start' &&
      plan.kind !== 'command' &&
      !materialName(plan.kind, plan.patch)
    )
      throw new Error('새 재료마다 이름을 선택해 주세요.')
    if (plan.kind === 'command' && !plan.patch.command?.key)
      throw new Error('새 명령어마다 키를 선택해 주세요.')
  }
  const oldWork =
    target.kind === 'work' ? (original as Chat | undefined) : undefined
  if (isStudioWorkLocked(oldWork))
    throw new Error('현재 서버에서 편집이 잠긴 작품에는 적용할 수 없어요.')
  if (oldWork && oldWork.characterIds.length + group('character').length > 6)
    throw new Error('기존 출연진을 포함해 캐릭터는 최대 6명이에요.')
  if (
    group('character').length > 6 ||
    (oldWork?.starts.length ?? 0) + group('start').length > 5
  )
    throw new Error('캐릭터는 최대 6명, 시작 장면은 최대 5개예요.')
  const names = [
    ...(oldWork?.characterIds ?? [])
      .map((id) => useRenewStore.getState().characters[id]?.name)
      .filter(Boolean),
    ...group('character').map((plan) => plan.patch.character?.name),
  ]
  const assertSpeaker = (name: string) => {
    if (name && names.filter((item) => item === name).length !== 1)
      throw new Error(
        `“${name}” 캐릭터를 하나로 찾을 수 없어요. 이름과 포함할 출연진을 확인해 주세요.`
      )
  }
  if (root.leadName) assertSpeaker(root.leadName)
  for (const plan of group('start')) {
    assertSpeaker(plan.patch.start?.speaker ?? '')
    if (!names.length)
      throw new Error('첫 대사를 보낼 캐릭터를 먼저 포함해 주세요.')
  }
  const commandKeys = new Set<string>([
    ...SERVICE_COMMAND_KEYS,
    ...(oldWork?.commandIds ?? [])
      .map((id) => useRenewStore.getState().commands[id]?.key)
      .filter((key): key is string => !!key),
  ])
  const importedCommands = [
    ...group('command'),
    ...plans.filter((plan) => plan.root && plan.kind === 'command'),
  ]
  for (const plan of importedCommands) {
    const key = plan.patch.command?.key
    if (key && commandKeys.has(key))
      throw new Error(`“${key}”는 이미 사용 중인 명령어 키예요.`)
    if (key) commandKeys.add(key)
  }
  if (target.kind === 'command' && target.id && root.command?.key) {
    const state = useRenewStore.getState()
    const key = root.command.key
    const clash = Object.values(state.chats).some(
      (work) =>
        work.commandIds.includes(target.id!) &&
        (work.officialCommands.includes(key) ||
          work.commandIds.some(
            (id) => id !== target.id && state.commands[id]?.key === key
          ))
    )
    if (clash) throw new Error('연결된 작품에 같은 명령어 키가 있어요.')
  }
  if (
    (oldWork?.commandIds.length ?? 0) +
      (oldWork?.officialCommands.length ?? 0) +
      group('command').length >
    20
  )
    throw new Error('작품의 명령어는 벨루빗 명령어를 포함해 최대 20개예요.')
  if (target.kind === 'world' && original && root.world) {
    const old = original as World
    if (root.world.keywords)
      root.world.keywords = [...old.keywords, ...root.world.keywords]
    if ((root.world.keywords?.length ?? 0) > 50)
      throw new Error('기존 키워드북과 합쳐 최대 50개예요.')
  }
  const importedStatusHtml = root.status?.html !== undefined
  if (
    target.kind === 'status' &&
    original &&
    root.status?.fields &&
    !importedStatusHtml
  ) {
    const old = original as StatusBoard
    const theme = STATUS_THEMES.find((item) => item.id === old.design?.theme)
    if (theme && !old.design?.edited)
      root.status.html = theme.render(
        root.status.fields,
        old.design?.accent ?? ''
      )
  }
  if (
    target.kind === 'status' &&
    root.status &&
    importedStatusHtml &&
    original &&
    (original as StatusBoard).design
  )
    root.status.design = { ...(original as StatusBoard).design!, edited: true }
  const create = async (kind: EditorKind, patch: ImportPatch) => {
    if (kind === 'world')
      return createWorld({ ...patch.world, visibility: 'private' })
    if (kind === 'character') return createCharacter(patch.character)
    if (kind === 'status') return createStatus(patch.status)
    if (kind === 'command') return createCommand(patch.command)
    return createChat(patch.work?.characterIds ?? [], {
      ...patch.work,
      visibility: 'private',
    })
  }
  return {
    async run() {
      current()
      if (applied) {
        if (!matchesPatch(activeTarget, root))
          throw new Error('적용 후 원본이 변경돼 저장을 재시도하지 않았어요.')
        await flushAll(keys, { retryFailed: true })
        current()
        return activeTarget.id!
      }
      unchanged()
      for (const kind of ['world', 'character', 'status', 'command'] as const) {
        for (const plan of group(kind)) {
          if (ids.has(plan.key)) continue
          unchanged()
          if (kind === 'character') {
            const world = group('world')[0]
            if (world && ids.get(world.key))
              plan.patch.character = {
                ...plan.patch.character,
                worldMode: 'world',
                worldId: ids.get(world.key)!,
              }
          }
          const id = await create(kind, plan.patch)
          ids.set(plan.key, id)
          unchanged()
        }
      }
      const worldId = group('world')[0]
        ? ids.get(group('world')[0].key)
        : undefined
      if (target.kind === 'character' && worldId)
        root.character = { ...root.character, worldMode: 'world', worldId }
      if (target.kind === 'work') {
        const added = group('character').map((plan) => ids.get(plan.key)!)
        const characterIds = [...(oldWork?.characterIds ?? []), ...added]
        const findSpeaker = (name: string) => {
          const found = characterIds.filter(
            (id) => useRenewStore.getState().characters[id]?.name === name
          )
          if (found.length !== 1)
            throw new Error(
              `“${name}” 캐릭터가 변경됐어요. 만들어진 재료를 확인한 뒤 연결해 주세요.`
            )
          return found[0]
        }
        const lead = root.leadName
          ? findSpeaker(root.leadName)
          : oldWork?.lead || characterIds[0] || null
        const starts: Start[] = group('start').map((plan) => {
          const start = plan.patch.start!
          return {
            id: newRowId(),
            name: start.name,
            place: start.place,
            suggestions: start.suggestions,
            intro: {
              context: start.context,
              lines: [
                {
                  id: newRowId(),
                  who: 'char',
                  charId: start.speaker
                    ? findSpeaker(start.speaker)
                    : lead || undefined,
                  text: start.greeting,
                },
              ],
            },
          }
        })
        root.work = {
          ...root.work,
          ...(added.length ? { characterIds } : {}),
          ...(lead && (root.leadName || !oldWork?.lead) ? { lead } : {}),
          ...(worldId ? { worldId } : {}),
          ...(group('status')[0]
            ? { statusId: ids.get(group('status')[0].key)! }
            : {}),
          ...(group('command').length
            ? {
                commandIds: [
                  ...(oldWork?.commandIds ?? []),
                  ...group('command').map((plan) => ids.get(plan.key)!),
                ],
              }
            : {}),
          ...(starts.length
            ? { starts: [...(oldWork?.starts ?? []), ...starts] }
            : {}),
        }
      }
      unchanged()
      if (!target.id) {
        const id = await create(target.kind, root)
        activeTarget = { kind: target.kind, id }
        current()
      } else {
        if (target.kind === 'character')
          undoPatch = {
            character: previous(original as Character, root.character ?? {}),
          }
        if (target.kind === 'world')
          undoPatch = { world: previous(original as World, root.world ?? {}) }
        if (target.kind === 'status')
          undoPatch = {
            status: previous(original as StatusBoard, root.status ?? {}),
          }
        if (target.kind === 'command')
          undoPatch = {
            command: previous(original as Command, root.command ?? {}),
          }
        if (target.kind === 'work')
          undoPatch = { work: previous(original as Chat, root.work ?? {}) }
        update(activeTarget, root)
      }
      applied = true
      keys = [
        `${target.kind === 'work' ? 'chat' : target.kind}:${activeTarget.id}`,
        ...related
          .filter((plan) => plan.kind !== 'start')
          .map((plan) => `${plan.kind}:${ids.get(plan.key)}`),
      ]
      await flushAll(keys, { retryFailed: true })
      current()
      return activeTarget.id!
    },
    async undo() {
      current()
      if (!undoPatch || !target.id || !applied)
        throw new Error(
          '되돌릴 원본 변경이 없어요. 새 재료는 목록에서 확인해 주세요.'
        )
      if (!restored) {
        if (!matchesPatch(activeTarget, root))
          throw new Error('이후 수정한 항목이 있어 자동으로 되돌리지 않았어요.')
        update(activeTarget, undoPatch)
        restored = true
      } else if (!matchesPatch(activeTarget, undoPatch))
        throw new Error('되돌린 후 내용이 변경됐어요.')
      await flushAll([keys[0]], { retryFailed: true })
      current()
    },
    get createdCount() {
      return ids.size
    },
  }
}
